import { useEffect, useMemo, useState } from "react";
import type { AdvertisementDto, AdvertisementSpendEntryDto } from "@/api/advertising";
import { extractErrorMessage } from "@/api/client";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import {
  Button,
  Input,
  Label,
  MissingValue,
  Skeleton,
  StatusBadge,
  SubTable,
  Surface,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import {
  useAdjustAdvertisementSpend,
  useAdvertisementSpend,
  useRecordAdvertisementSpend,
} from "@/hooks/buyers/useAdvertising";
import { currentPeriod, money, periodLabel, periodStartIso } from "./advertisingBits";

interface Props {
  open: boolean;
  onClose: () => void;
  advertisement: AdvertisementDto;
  /** Whether this caller may append to the ledger at all. */
  canRecord: boolean;
  /** Cross-team authority. Correcting a recorded cost is deliberately stricter
   *  than recording one — see the server-side scope rule. */
  canCorrect: boolean;
}

/** How a cost is dated: to a whole month (the common case — an invoice or a
 *  platform statement covers a month) or to one day. */
type EntryMode = "month" | "day";

/** One month of the ledger, with the rows beneath it and their net. */
export interface SpendMonthGroup {
  /** `YYYY-MM`. */
  period: string;
  net: number;
  entries: AdvertisementSpendEntryDto[];
}

/** Newest month first; rows inside a month newest first. */
export function groupSpendByMonth(entries: readonly AdvertisementSpendEntryDto[]): SpendMonthGroup[] {
  const map = new Map<string, SpendMonthGroup>();
  for (const e of entries) {
    const period = e.spendDate.slice(0, 7);
    const g = map.get(period) ?? { period, net: 0, entries: [] };
    g.net += e.amount;
    g.entries.push(e);
    map.set(period, g);
  }
  return Array.from(map.values())
    .map((g) => ({ ...g, entries: [...g.entries].sort((a, b) => b.spendDate.localeCompare(a.spendDate)) }))
    .sort((a, b) => b.period.localeCompare(a.period));
}

/**
 * One advertisement's spend ledger (WS6; monthly entry D3).
 *
 * <p>🔴 <b>Append-only.</b> There is no edit and no delete for a recorded cost;
 * a correction is an OFFSETTING ROW pointing at what it corrects, and the total
 * is the sum. That is what keeps "what did we believe this cost, and when"
 * legible after somebody fixes a typo.</p>
 *
 * <p><b>A month's cost posts as ONE row dated to the first of that month</b>
 * ("A whole month" in the entry-dated-to select) — the same record endpoint
 * the day path uses,
 * so the ledger, the budget pace and the cost-per-lead report all read it
 * without a second shape. "Set a month's total" posts a correction against
 * the month's first cost row for the difference.</p>
 *
 * <p><b>Every figure is typed by a person.</b> This system has no
 * advertising-platform integration of any kind, so the surface says so rather
 * than letting a reader assume the numbers arrived on their own.</p>
 */
export function SpendLedgerModal({
  open, onClose, advertisement, canRecord, canCorrect,
}: Props) {
  const tz = useUserTimeZone();
  const ledger = useAdvertisementSpend(open ? advertisement.id : null);
  const record = useRecordAdvertisementSpend();
  const adjust = useAdjustAdvertisementSpend();

  const [mode, setMode] = useState<EntryMode>("day");
  const [month, setMonth] = useState("");
  const [spendDate, setSpendDate] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [correcting, setCorrecting] = useState<AdvertisementSpendEntryDto | null>(null);
  /** A "set the month's total" correction: the delta is computed from the net. */
  const [monthTarget, setMonthTarget] = useState<SpendMonthGroup | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setCorrecting(null);
    setMonthTarget(null);
    // Opens on the day path: `todaySeedsInViewerZone` pins that this modal
    // seeds a date input with the VIEWER's today on open, and the month path
    // is one select away rather than a second default to keep in step.
    setMode("day");
    setAmount("");
    setNotes("");
    setMonth(currentPeriod());
    setSpendDate(isoDateInTz(new Date(), tz));
  }, [open, tz]);

  const groups = useMemo(() => groupSpendByMonth(ledger.data?.entries ?? []), [ledger.data]);

  const parsed = amount.trim() === "" ? null : Number(amount);
  const pending = record.isPending || adjust.isPending;

  // A correction may be negative — that is how money comes back out. An
  // ordinary entry may not: a negative cost that is not a correction is a typo,
  // and the ledger's whole value is that every row means something. A month
  // total is a NEW total (non-negative); its delta may go either way.
  const delta = monthTarget && parsed !== null ? parsed - monthTarget.net : null;
  const amountValid =
    parsed !== null && Number.isFinite(parsed)
    && (monthTarget ? parsed >= 0 && delta !== 0
      : correcting ? parsed !== 0 : parsed > 0);

  const effectiveDate =
    correcting ? spendDate
    : monthTarget ? periodStartIso(monthTarget.period)
    : mode === "month" ? (month ? periodStartIso(month) : "")
    : spendDate;

  const reasonRequired = correcting !== null || monthTarget !== null;
  const canSubmit =
    canRecord && amountValid && effectiveDate !== "" && !pending
    && (!reasonRequired || notes.trim().length > 0);

  function resetForm() {
    setAmount("");
    setNotes("");
    setCorrecting(null);
    setMonthTarget(null);
  }

  async function submit() {
    setError(null);
    try {
      if (correcting) {
        await adjust.mutateAsync({
          id: advertisement.id,
          payload: {
            adjustsSpendEntryId: correcting.id,
            amount: parsed!,
            spendDate: spendDate || null,
            reason: notes.trim(),
          },
        });
      } else if (monthTarget) {
        // The anchor is the month's oldest cost row; the delta lands on the
        // first of the month so it nets inside the same period.
        const anchor = [...monthTarget.entries].reverse().find((e) => !e.isAdjustment)
          ?? monthTarget.entries[monthTarget.entries.length - 1];
        await adjust.mutateAsync({
          id: advertisement.id,
          payload: {
            adjustsSpendEntryId: anchor.id,
            amount: delta!,
            spendDate: periodStartIso(monthTarget.period),
            reason: notes.trim(),
          },
        });
      } else {
        await record.mutateAsync({
          id: advertisement.id,
          payload: { spendDate: effectiveDate, amount: parsed!, notes: notes.trim() || null },
        });
      }
      resetForm();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }

  function beginCorrection(entry: AdvertisementSpendEntryDto) {
    setMonthTarget(null);
    setCorrecting(entry);
    // Pre-filled with the exact reversal, which is the common case, and dated to
    // the corrected entry's own month so a fix lands where it belongs rather
    // than where it was noticed.
    setAmount(String(-entry.amount));
    setSpendDate(entry.spendDate);
    setNotes("");
  }

  function beginMonthTotal(group: SpendMonthGroup) {
    setCorrecting(null);
    setMonthTarget(group);
    setAmount(String(group.net));
    setNotes("");
  }

  const columns: SubTableColumn<AdvertisementSpendEntryDto>[] = [
    {
      key: "date",
      header: "Date",
      render: (r) => formatCalendarDate(r.spendDate),
      sortAccessor: (r) => r.spendDate,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (r) => money(r.amount),
      sortAccessor: (r) => r.amount,
    },
    {
      key: "kind",
      header: "Kind",
      render: (r) =>
        r.isAdjustment
          ? <StatusBadge tone="warn">Correction</StatusBadge>
          : <span className="text-gray-mid">Cost</span>,
    },
    {
      key: "who",
      header: "Recorded by",
      render: (r) => r.recordedByDisplayName ?? <MissingValue />,
    },
    {
      key: "notes",
      header: "Note",
      render: (r) => r.notes ?? <MissingValue />,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) =>
        canCorrect && !pending ? (
          <Button variant="text" size="sm" onClick={() => beginCorrection(r)}>Correct</Button>
        ) : null,
    },
  ];

  const heading =
    correcting ? "Post a correction"
    : monthTarget ? `Set the total for ${periodLabel(monthTarget.period)}`
    : mode === "month" ? "Record a month's spend"
    : "Record a cost";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Spend — ${advertisement.name}`}
      size="xl"
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      }
    >
      <Surface kind="drawer">
      <div className="space-y-space-4">
        <p className="text-caption text-gray-mid">
          Recorded by hand — this system has no advertising-platform
          integration. Corrections are offsetting rows, never edits, so the
          history of what was believed stays legible.
        </p>

        {canRecord && (
          <div className="space-y-space-3 rounded-md border border-border-light p-space-3">
              <div className="flex items-baseline justify-between gap-space-2">
                <h3 className="font-medium text-onyx">{heading}</h3>
                {(correcting || monthTarget) ? (
                  <Button variant="text" size="sm" onClick={resetForm}>
                    Cancel correction
                  </Button>
                ) : (
                  <Select
                    aria-label="Entry dated to"
                    value={mode}
                    onChange={(e) => setMode(e.target.value as EntryMode)}
                    className="w-auto"
                  >
                    <option value="day">A specific day</option>
                    <option value="month">A whole month</option>
                  </Select>
                )}
              </div>

              {correcting && (
                <p className="text-caption text-gray-mid">
                  Correcting {money(correcting.amount)} dated{" "}
                  {formatCalendarDate(correcting.spendDate)}. A negative amount
                  takes money back out; a positive one adds what was
                  under-recorded.
                </p>
              )}
              {monthTarget && (
                <p className="text-caption text-gray-mid">
                  {periodLabel(monthTarget.period)} currently nets{" "}
                  {money(monthTarget.net)}. Enter the corrected total; the
                  difference posts as a correction dated to the first of the month.
                </p>
              )}

              <div className="grid gap-space-3 sm:grid-cols-3">
                {correcting ? (
                  <div>
                    <Label htmlFor="spend-date">Date</Label>
                    <Input
                      id="spend-date" type="date" value={spendDate}
                      onChange={(e) => setSpendDate(e.target.value)}
                    />
                  </div>
                ) : monthTarget ? (
                  <div>
                    <Label htmlFor="spend-month">Month</Label>
                    <Input id="spend-month" type="month" value={monthTarget.period} disabled />
                  </div>
                ) : mode === "month" ? (
                  <div>
                    <Label htmlFor="spend-month">Month</Label>
                    <Input
                      id="spend-month" type="month" value={month}
                      onChange={(e) => setMonth(e.target.value)}
                    />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="spend-date">Date</Label>
                    <Input
                      id="spend-date" type="date" value={spendDate}
                      onChange={(e) => setSpendDate(e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="spend-amount">
                    {monthTarget ? "New month total" : "Amount"}
                  </Label>
                  <Input
                    id="spend-amount" type="text" inputMode="decimal" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="spend-notes">
                    {reasonRequired ? "Reason (required)" : "Note"}
                  </Label>
                  <Input
                    id="spend-notes" value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <p role="alert" className="text-caption text-status-error">{error}</p>
              )}

              <div className="flex justify-end">
                <Button variant="primary" onClick={submit} disabled={!canSubmit}>
                  {pending ? "Saving…"
                    : correcting ? "Post correction"
                    : monthTarget ? "Post month correction"
                    : "Record"}
                </Button>
            </div>
          </div>
        )}

        {ledger.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="space-y-space-4">
            <div className="flex items-baseline justify-between">
              <span className="text-caption uppercase tracking-wide text-gray-mid">
                Total recorded
              </span>
              <span className="font-display text-h3 text-onyx">
                {money(ledger.data?.total ?? 0)}
              </span>
            </div>

            {groups.length === 0 ? (
              <SubTable
                caption={`Spend entries for ${advertisement.name}`}
                columns={columns}
                rows={[]}
                rowKey={(r) => r.id}
                empty={{
                  message: canRecord
                    ? "No spend recorded yet. Record what this advertisement has cost so its return can be measured."
                    : "No spend recorded yet.",
                }}
              />
            ) : (
              groups.map((g) => (
                <section key={g.period} className="space-y-space-2" data-testid={`spend-month-${g.period}`}>
                  <div className="flex items-baseline justify-between gap-space-2">
                    <h4 className="text-lead font-medium text-onyx m-0">
                      {periodLabel(g.period)}
                      <span className="ml-space-2 text-caption text-gray-mid">
                        net {money(g.net)}
                      </span>
                    </h4>
                    {canRecord && canCorrect && !pending && (
                      <Button variant="text" size="sm" onClick={() => beginMonthTotal(g)}>
                        Set month total
                      </Button>
                    )}
                  </div>
                  <SubTable
                    caption={`Spend entries for ${advertisement.name} in ${periodLabel(g.period)}`}
                    columns={columns}
                    rows={g.entries}
                    rowKey={(r) => r.id}
                  />
                </section>
              ))
            )}
          </div>
        )}
      </div>
      </Surface>
    </Modal>
  );
}
